import { COACHMARK_HIGHLIGHT_CLASS } from 'src/components/Coachmark';
import { CoachmarkTourKey } from 'src/store/ui/types';
import { pollForElement } from 'src/utils/DomUtils';

// The first client's row-actions button (the Actions column is pinned right).
const FIRST_ROW_ACTIONS_SELECTOR =
  '[row-index="0"] [data-testid="more-actions-menu"]';
const FIRST_ROW_SELECTOR = '.ag-row[row-index="0"]';
const OPEN_MENU_ITEM_SELECTOR = `[class~="${COACHMARK_HIGHLIGHT_CLASS}"]`;
const MENU_REOPEN_POLL_MS = 200;
export const CRM_CLIENTS_TOUR_KEY = 'crmClients' satisfies CoachmarkTourKey;

export const enterCoachmarkRowStep = (): (() => void) => {
  let highlighted: HTMLElement[] = [];

  const cancelPoll = pollForElement(
    () => {
      const rows = Array.from(
        document.querySelectorAll(FIRST_ROW_SELECTOR),
      ).filter((row): row is HTMLElement => row instanceof HTMLElement);
      return rows.length > 0 ? rows : null;
    },
    (rows) => {
      rows.forEach((row) => {
        row.classList.add(COACHMARK_HIGHLIGHT_CLASS);
      });
      highlighted = rows;
    },
  );

  return () => {
    cancelPoll();
    highlighted.forEach((row) => {
      row.classList.remove(COACHMARK_HIGHLIGHT_CLASS);
    });
  };
};

export const createEnterCoachmarkMenuStep =
  (setActive: (active: boolean) => void, isActive: () => boolean) =>
  (): (() => void) => {
    setActive(true);

    const openMenu = (): void => {
      const button = document.querySelector(FIRST_ROW_ACTIONS_SELECTOR);
      if (button instanceof HTMLElement) {
        button.click();
      }
    };

    const frame = requestAnimationFrame(openMenu);

    // While the tour points at it, the menu renders non-modal (see
    // ClientActionRenderer): click-away no longer closes it and the page behind
    // stays usable, so no dismiss guard is needed here. This backstop only covers a
    // close the tour doesn't expect (e.g. a programmatic one), reopening the menu so
    // the step stays completable. It no-ops while the menu is open — the norm.
    const reopenInterval = setInterval(() => {
      if (isActive() && !document.querySelector(OPEN_MENU_ITEM_SELECTOR)) {
        openMenu();
      }
    }, MENU_REOPEN_POLL_MS);

    return () => {
      cancelAnimationFrame(frame);
      clearInterval(reopenInterval);
      setActive(false);
      // The menu was opened programmatically (button.click), so the button's
      // active highlight never clears via mouse-leave/click-away — and completing
      // the tour via "Open portal" opens a new tab that steals focus before
      // click-away could fire. Nudge the button's own mouse-leave path so it does
      // not stay highlighted after the tour ends.
      const button = document.querySelector(FIRST_ROW_ACTIONS_SELECTOR);
      button?.dispatchEvent(
        new MouseEvent('mouseout', {
          bubbles: true,
          relatedTarget: document.body,
        }),
      );
    };
  };
